import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { AlertCircle, FileText, Eye, EyeOff } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API_BASE } from '../config';
import { useSwrStore, useSwrQuery } from '../swrCache';
import { todayStamp } from '../excel';
import DataFreshness from '../components/DataFreshness';
import MultiSelectCombo from '../components/MultiSelectCombo';
import DrillDownModal from '../components/DrillDownModal';
import SimpleDataTable from '../components/SimpleDataTable';
import { formatDate as fmtDate } from '../utils/formatDate';

/* ============================================================================
 * Supply Chain Analytics Dashboard — /po-so-lifecycle/supply-chain-dashboard
 *
 * Management rollup sitting above PO Tracker: one filter bar drives every
 * card on the page, backed by a single aggregate endpoint
 * (backend/src/functions/supply-chain-dashboard.js) built specifically for
 * this page over the same po_headers/so_headers/invoice_headers/
 * grn_management/shiprocket_mis_shipments/kam_vendor tables PO Tracker reads.
 *
 * Visual language matches GRNManagement.jsx's tokens (Space Grotesk,
 * #FAF8FC field, white #ECE7F2-bordered cards, #231C30 dark chips, #6E58A6
 * purple accent); infra (SWR cache, DataFreshness, Excel export) follows
 * POTracker.jsx's established patterns.
 * ========================================================================== */

const T = {
  page: '#FAF8FC', surface: '#fff', surfaceSubtle: '#FBFAFD',
  border: '#ECE7F2', borderSoft: '#E4DEEE',
  ink: '#231C30', headerBar: '#0B0A12', muted: '#8C82A0', faint: '#9A90AE',
  purple: '#6E58A6', purpleMid: '#8E79BE', purpleLight: '#C9B7EA', purpleTint: '#EEE9F8',
  green: '#5C7E60', greenTint: '#E7F0E8', greenBorder: '#D6E6D8', greenLight: '#9FC7A6',
  amber: '#9A7B3E', amberTint: '#F5EDDC', amberBorder: '#EADFC5',
  blue: '#3A6B8A', blueTint: '#DCEAF5', blueBorder: '#C3D9EA', blueLight: '#A8C7DE',
  error: '#C25E77', errorTint: '#F7E5EA', errorBorder: '#EFD2DA',
  onDarkInk: '#F0EFF5', onDarkSub: '#B9AEDD',
};
const FONT = "'Space Grotesk', sans-serif";

// Builds YYYY-MM-DD from the Date object's LOCAL fields, never through
// toISOString() (which converts to UTC first — on a browser running in IST,
// local midnight Aug 1 becomes 2026-07-31T18:30Z, silently shifting every
// date sent to the backend back by one day).
function isoDate(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
const TODAY = isoDate(new Date());
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }
function fyStartYear() { const n = new Date(); return n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; }
function fyStartDate() { return isoDate(new Date(fyStartYear(), 3, 1)); }

// today - N days, inclusive of today, so "Last 30 days" is exactly 30
// calendar days total (today counts as one of them) — daysAgo(29), not
// daysAgo(30), which would make it a 31-day span.
const QUICK_RANGES = [
  { key: 'today', label: 'Today', from: TODAY, to: TODAY },
  { key: 'd30', label: 'Last 30 days', from: daysAgo(29), to: TODAY },
  { key: 'd60', label: 'Last 60 days', from: daysAgo(59), to: TODAY },
  { key: 'd90', label: 'Last 90 days', from: daysAgo(89), to: TODAY },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Rolling window (not locked to the current fiscal year, unlike the old
// FY_MONTHS list) — the last 24 months up to and including the current one,
// newest first, so a past month like "Jan 2026" stays pickable even once the
// FY has rolled over. Recomputed fresh every render from `new Date()`, so it
// keeps rolling forward on its own with no per-year maintenance.
function rollingMonthOptions() {
  const now = new Date();
  const options = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yr = d.getFullYear(), m = d.getMonth() + 1;
    options.push({ key: `${yr}-${String(m).padStart(2, '0')}`, label: `${MONTH_NAMES[m - 1]} ${yr}`, year: yr, month: m });
  }
  return options;
}
// 'All Months' replaces the old 'FY to date' quick-range chip — same
// resolved range (April 1 -> today), just relocated into the Month dropdown.
// Mutually exclusive with the individual months below (see toggleMonth).
const ALL_MONTHS_LABEL = 'All Months';
const MONTH_OPTIONS = rollingMonthOptions();
const MONTH_LABELS = [ALL_MONTHS_LABEL, ...MONTH_OPTIONS.map((m) => m.label)];

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  return `₹${(n / 1e5).toFixed(1)} L`;
}
function fmtInt(v) { return v === null || v === undefined ? '—' : Number(v).toLocaleString('en-IN'); }
function fmtDays(v) { return v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}`; }
function fmtHours(v) { return v === null || v === undefined ? '—' : `${fmtInt(v)}h`; }

// Column sets + titles for each of the dashboard's click-a-card drill-downs
// (DrillDownModal + SimpleDataTable render whichever of these is active).
const DRILLDOWN_DEFS = {
  expiring_soon: {
    title: 'PO Expiring Soon',
    subtitle: 'Expiry within SLA window, SO not yet made · sorted by expiry date',
    columns: [
      { key: 'po_number', label: 'PO Number', align: 'left' },
      { key: 'party_name', label: 'Party', align: 'left' },
      { key: 'location', label: 'Location', align: 'left' },
      { key: 'po_expiry_date', label: 'Expiry Date', align: 'left', render: (r) => fmtDate(r.po_expiry_date) },
    ],
  },
  in_transit: {
    title: 'In Transit > SLA',
    subtitle: 'Dispatched, no delivery scan yet',
    columns: [
      { key: 'po_number', label: 'PO Number', align: 'left' },
      { key: 'party_name', label: 'Party', align: 'left' },
      { key: 'location', label: 'Location', align: 'left' },
      { key: 'tracking_status', label: 'Status', align: 'left' },
      { key: 'days_in_transit', label: 'Days in Transit' },
    ],
  },
  at_destination: {
    title: 'At Destination > SLA',
    subtitle: 'Reached but not yet marked delivered',
    columns: [
      { key: 'po_number', label: 'PO Number', align: 'left' },
      { key: 'party_name', label: 'Party', align: 'left' },
      { key: 'location', label: 'Location', align: 'left' },
      { key: 'tracking_status', label: 'Status', align: 'left' },
      { key: 'hours_at_destination', label: 'Hours at Destination', render: (r) => fmtHours(r.hours_at_destination) },
    ],
  },
  open_discrepancies: {
    title: 'Discrepancies Open',
    subtitle: 'GRN rows with an open discrepancy',
    columns: [
      { key: 'po_no', label: 'PO No', align: 'left' },
      { key: 'grn_no', label: 'GRN No', align: 'left' },
      { key: 'party_name', label: 'Party', align: 'left' },
      { key: 'location', label: 'Location', align: 'left' },
      { key: 'grn_date', label: 'GRN Date', align: 'left', render: (r) => fmtDate(r.grn_date) },
      { key: 'status', label: 'Status', align: 'left' },
    ],
  },
  tickets: {
    title: 'Tickets Open',
    subtitle: 'Discrepancies with a ticket raised, not yet resolved',
    columns: [
      { key: 'po_no', label: 'PO No', align: 'left' },
      { key: 'grn_no', label: 'GRN No', align: 'left' },
      { key: 'party_name', label: 'Party', align: 'left' },
      { key: 'location', label: 'Location', align: 'left' },
      { key: 'ticket_no', label: 'Ticket No', align: 'left' },
      { key: 'ticket_open_date', label: 'Opened', align: 'left', render: (r) => fmtDate(r.ticket_open_date) },
      { key: 'age_days', label: 'Age (days)' },
    ],
  },
  stuck: {
    columns: [
      { key: 'po_number', label: 'PO Number', align: 'left' },
      { key: 'party_name', label: 'Party', align: 'left' },
      { key: 'location', label: 'Location', align: 'left' },
      { key: 'po_date', label: 'PO Date', align: 'left', render: (r) => fmtDate(r.po_date) },
      { key: 'days_stuck', label: 'Days Stuck' },
    ],
  },
};

function toneFor(value, target) {
  if (value === null || value === undefined || !target) return T.muted;
  if (value <= target) return T.green;
  if (value <= target * 1.25) return T.amber;
  return T.error;
}

const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, boxShadow: '0 1px 2px rgba(35,28,48,0.05)' };
const label10 = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.muted };
const note = { fontSize: 11.5, color: T.muted };

export default function SupplyChainDashboard() {
  const [dateMode, setDateMode] = useState('quick');
  const [quick, setQuick] = useState('d30');
  const [monthsSelected, setMonthsSelected] = useState([]); // array of MONTH_LABELS entries
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [locations, setLocations] = useState([]);
  const [kams, setKams] = useState([]);
  const [parties, setParties] = useState([]);
  const [rankMode, setRankMode] = useState('party');
  const [chartMonthKey, setChartMonthKey] = useState(null);
  const [showVariance, setShowVariance] = useState(false);
  const dashboardRef = useRef(null);

  // Always an array of {from,to} pairs, even for the single-range modes —
  // the month filter is multi-select and picked months can be
  // non-consecutive (Jan + Mar, skipping Feb), which a single from/to pair
  // can't express. `overallFrom`/`overallTo` (chronological min/max across
  // every range) is only for the From/To display fields below — the actual
  // filtering always uses the full `ranges` list, never just the span.
  const resolvedRanges = useMemo(() => {
    if (dateMode === 'quick') {
      const q = QUICK_RANGES.find((r) => r.key === quick) || QUICK_RANGES[1];
      return { ranges: [{ from: q.from, to: q.to }], label: q.label, overallFrom: q.from, overallTo: q.to };
    }
    if (dateMode === 'month') {
      if (monthsSelected.includes(ALL_MONTHS_LABEL)) {
        return { ranges: [{ from: fyStartDate(), to: TODAY }], label: 'All Months (FY to date)', overallFrom: fyStartDate(), overallTo: TODAY };
      }
      if (monthsSelected.length === 0) return { ranges: [], label: '', overallFrom: '', overallTo: '' };
      const now = new Date();
      const curYear = now.getFullYear(), curMonth = now.getMonth() + 1;
      const ranges = monthsSelected.map((label) => {
        const m = MONTH_OPTIONS.find((o) => o.label === label);
        if (!m) return null;
        const from = isoDate(new Date(m.year, m.month - 1, 1));
        const isCurrentMonth = m.year === curYear && m.month === curMonth;
        const to = isCurrentMonth ? TODAY : isoDate(new Date(m.year, m.month, 0));
        return { from, to };
      }).filter(Boolean).sort((a, b) => a.from.localeCompare(b.from));
      const label = monthsSelected.length === 1 ? monthsSelected[0] : `${monthsSelected.length} months`;
      return { ranges, label, overallFrom: ranges[0]?.from || '', overallTo: ranges[ranges.length - 1]?.to || '' };
    }
    const label = fromDate || toDate
      ? `${fromDate ? fromDate.split('-').reverse().join('/') : 'any'} → ${toDate ? toDate.split('-').reverse().join('/') : 'any'}`
      : '';
    return { ranges: [{ from: fromDate || '', to: toDate || '' }], label, overallFrom: fromDate || '', overallTo: toDate || '' };
  }, [dateMode, quick, monthsSelected, fromDate, toDate]);

  const rangeKey = useMemo(() => {
    const qs = new URLSearchParams();
    const validRanges = resolvedRanges.ranges.filter((r) => r.from && r.to);
    if (validRanges.length) qs.set('date_ranges', validRanges.map((r) => `${r.from}:${r.to}`).join(','));
    if (locations.length) qs.set('location', locations.join(','));
    if (kams.length) qs.set('kam', kams.join(','));
    if (parties.length) qs.set('party', parties.join(','));
    return qs.toString();
  }, [resolvedRanges, locations, kams, parties]);

  const store = useSwrStore(8);
  const query = useSwrQuery(store, rangeKey, useCallback(async () => {
    const res = await fetch(`${API_BASE}/supply-chain-dashboard${rangeKey ? '?' + rangeKey : ''}`);
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data?.error || `Server returned ${res.status}`);
    return data;
  }, [rangeKey]));

  // Drill-down modal: { type, bucket?, title, subtitle?, columns } while
  // open, null when closed. Fetched on demand (not through the SWR store —
  // these are one-off lookups tied to a click, not cached page data).
  const [drillDown, setDrillDown] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  useEffect(() => {
    if (!drillDown) return;
    let cancelled = false;
    setDrillLoading(true);
    setDrillError(null);
    const qs = new URLSearchParams(rangeKey);
    qs.set('type', drillDown.type);
    if (drillDown.bucket) qs.set('bucket', drillDown.bucket);
    fetch(`${API_BASE}/supply-chain-drilldown?${qs.toString()}`)
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (cancelled) return;
        if (!ok || !body.success) throw new Error(body?.error || 'Failed to load');
        setDrillRows(body.rows || []);
      })
      .catch((err) => { if (!cancelled) setDrillError(err.message); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [drillDown, rangeKey]);

  const closeDrillDown = () => { setDrillDown(null); setDrillRows([]); setDrillError(null); };

  const data = query.data;
  const loading = query.loading;

  const clearAll = () => {
    setDateMode('quick'); setQuick('d30'); setMonthsSelected([]); setFromDate(''); setToDate('');
    setLocations([]); setKams([]); setParties([]);
  };

  const pickQuick = (key) => { setDateMode('quick'); setQuick(key); setMonthsSelected([]); setFromDate(''); setToDate(''); };
  // "All Months" is mutually exclusive with picking specific months —
  // selecting one clears the other, rather than combining into a nonsensical
  // "all months + also just this one" selection.
  const toggleMonths = (nextValues) => {
    setDateMode('month'); setFromDate(''); setToDate('');
    const addedAll = nextValues.includes(ALL_MONTHS_LABEL) && !monthsSelected.includes(ALL_MONTHS_LABEL);
    const addedSpecific = nextValues.some((v) => v !== ALL_MONTHS_LABEL && !monthsSelected.includes(v));
    if (addedAll) setMonthsSelected([ALL_MONTHS_LABEL]);
    else if (addedSpecific) setMonthsSelected(nextValues.filter((v) => v !== ALL_MONTHS_LABEL));
    else setMonthsSelected(nextValues);
  };
  const pickFrom = (val) => { setDateMode('range'); setFromDate(val); setMonthsSelected([]); };
  const pickTo = (val) => { setDateMode('range'); setToDate(val); setMonthsSelected([]); };

  const activeChips = [];
  if (resolvedRanges.label) activeChips.push(resolvedRanges.label);
  locations.forEach((l) => activeChips.push(l));
  kams.forEach((k) => activeChips.push(k));
  parties.forEach((p) => activeChips.push(p));

  const captureCanvas = () => html2canvas(dashboardRef.current, { scale: 2, backgroundColor: '#FAF8FC', useCORS: true });

  const handleDownloadPdf = async () => {
    if (!dashboardRef.current) return;
    const canvas = await captureCanvas();
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: canvas.width > canvas.height ? 'l' : 'p', unit: 'px', format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`supply-chain-dashboard_${todayStamp()}.pdf`);
  };

  return (
    <div ref={dashboardRef} style={{ maxWidth: 1560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT, background: T.page, padding: '20px 22px 32px' }}>
      {/* Header */}
      <div style={{ background: T.headerBar, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, boxShadow: '0 6px 20px rgba(35,28,48,0.10)', padding: '14px 20px', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 300, color: '#F3EFFA', letterSpacing: '-0.01em', margin: 0 }}>
            Supply Chain <span style={{ fontWeight: 600, color: T.purpleLight }}>Analytics Dashboard</span>
          </h1>
          <div style={{ fontSize: 11.5, color: T.onDarkSub, marginTop: 3 }}>
            Purchase Order Tracker · Sales Orders · Invoicing · Goods Received Note
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <DataFreshness fetchedAt={query.fetchedAt} loading={loading} onRefresh={query.refresh} style={{ color: T.onDarkSub }} />
          <button onClick={handleDownloadPdf} disabled={!data} style={{ background: T.green, color: '#fff', padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500, border: 'none', cursor: data ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6, opacity: data ? 1 : 0.5 }}>
            <FileText size={14} /> Download PDF
          </button>
        </div>
      </div>

      {query.error && (
        <div style={{ background: T.errorTint, color: T.error, borderRadius: 10, padding: '12px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={16} /> {query.error}
        </div>
      )}

      {/* Filter bar — one row, horizontally scrollable if the viewport is too
          narrow to fit every control (small monitors / laptops) rather than
          wrapping into a second row or cramming controls unreadably small. */}
      <div style={{ ...card, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', alignItems: 'flex-end', overflowX: 'auto', paddingBottom: 2 }}>
          <FieldStack label="QUICK RANGE">
            <div data-tutorial="scm-quick-range" style={{ padding: 4, borderRadius: 999, background: T.surfaceSubtle, border: `1px solid ${T.border}`, display: 'flex', gap: 2, flexShrink: 0 }}>
              {QUICK_RANGES.map((r) => (
                <button key={r.key} onClick={() => pickQuick(r.key)}
                  style={{ padding: '8px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                    background: dateMode === 'quick' && quick === r.key ? T.ink : 'transparent',
                    color: dateMode === 'quick' && quick === r.key ? '#fff' : T.muted }}>
                  {r.label}
                </button>
              ))}
            </div>
          </FieldStack>
          <FieldStack label="MONTH">
            <MultiSelectCombo label="Month" options={MONTH_LABELS} values={dateMode === 'month' ? monthsSelected : []} onChange={toggleMonths} placeholder="Choose months" width={170} />
          </FieldStack>
          <FieldStack label="FROM">
            <input type="date" value={dateMode === 'range' ? fromDate : resolvedRanges.overallFrom} onChange={(e) => pickFrom(e.target.value)} style={{ ...selectStyle(128), padding: '8px 10px', fontWeight: 400 }} />
          </FieldStack>
          <FieldStack label="TO">
            <input type="date" value={dateMode === 'range' ? toDate : resolvedRanges.overallTo} onChange={(e) => pickTo(e.target.value)} style={{ ...selectStyle(128), padding: '8px 10px', fontWeight: 400 }} />
          </FieldStack>
          <FieldStack label="LOCATION">
            <MultiSelectCombo label="Location" options={data?.filter_options?.locations || []} values={locations} onChange={setLocations} placeholder="All locations" width={170} />
          </FieldStack>
          <FieldStack label="KAM">
            <MultiSelectCombo label="KAM" options={data?.filter_options?.kams || []} values={kams} onChange={setKams} placeholder="All KAMs" width={150} />
          </FieldStack>
          <FieldStack label="PARTY">
            <MultiSelectCombo label="Party" options={data?.filter_options?.parties || []} values={parties} onChange={setParties} placeholder="All parties" width={170} />
          </FieldStack>
          <button onClick={clearAll} style={{ background: T.purpleTint, color: T.purple, border: `1px solid ${T.borderSoft}`, padding: '9px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Clear all
          </button>
        </div>
        <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, color: T.muted }}>Active:</span>
            {activeChips.map((c, i) => (
              <span key={i} style={{ padding: '5px 11px', borderRadius: 999, background: T.purpleTint, color: '#4C3B78', fontSize: 11.5, fontWeight: 600 }}>{c}</span>
            ))}
          </div>
          {data && (
            <div style={{ fontSize: 12.5 }}>
              <strong>{fmtInt(data.scope.po_count)}</strong> POs · <strong>{fmtInt(data.scope.units)}</strong> units · <strong>{fmtMoney(data.scope.amount)}</strong>
            </div>
          )}
        </div>
      </div>

      {!data && loading && <div style={{ ...card, padding: 46, textAlign: 'center', color: T.muted, fontSize: 14 }}>Loading…</div>}

      {data && (
        <>
          {/* Cycle time + funnel */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ ...card, flex: '3 1 520px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>PO Lifecycle Benchmarks</div>
                <div style={note}>Average days per hop</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 12, marginTop: 14 }}>
                <CycleStat label="PO → Invoice" value={data.cycle_times.po_to_invoice_days} target={data.config.cycle_target_po_to_invoice_days} />
                <CycleStat label="SO → Invoice" value={data.cycle_times.so_to_invoice_days} target={data.config.cycle_target_so_to_invoice_days} />
                <CycleStat label="PO → Dispatched" value={data.cycle_times.po_to_dispatched_days} target={data.config.cycle_target_po_to_dispatched_days} />
                <CycleStat label="PO → Delivered" value={data.cycle_times.po_to_delivered_days} target={data.config.cycle_target_po_to_delivered_days} />
                <CycleStat label="Invoice → Delivered" value={data.cycle_times.invoice_to_delivered_days} target={data.config.cycle_target_invoice_to_delivered_days} />
              </div>
            </div>
            <div style={{ background: T.ink, borderRadius: 16, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: '2 1 320px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.onDarkInk }}>PO → delivered</div>
              </div>
              <FunnelStage label="Total POs" count={data.scope.po_count} total={data.scope.po_count} color={T.onDarkInk}
                sub="in the current filtered scope" />
              <FunnelStage label="POs delivered" count={data.funnel.delivered} total={data.scope.po_count} color={T.blueLight}
                sub={`${pct(data.funnel.delivered, data.scope.po_count)}% of ${fmtInt(data.scope.po_count)} POs · ${fmtDays(data.cycle_times.po_to_delivered_days)}d avg`} />
              <FunnelStage label="POD received" count={data.funnel.pod_received} total={data.funnel.delivered || 1} color={T.purpleLight}
                sub={`${pct(data.funnel.pod_received, data.funnel.delivered)}% of delivered`} />
              <FunnelStage label="GRN received" count={data.funnel.grn_received} total={data.funnel.pod_received || 1} color={T.greenLight}
                sub={`${pct(data.funnel.grn_received, data.funnel.pod_received)}% of POD received`} />
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.10)', paddingTop: 10, fontSize: 11, color: T.onDarkSub }}>
                {fmtInt(data.funnel.delivered - data.funnel.pod_received)} delivered POs still without POD · {fmtInt(data.funnel.pod_received - data.funnel.grn_received)} delivered but not yet reconciled.
              </div>
            </div>
          </div>

          {/* Watchlist row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
            <AlertCard title="PO EXPIRING SOON" unit="POs" bg={T.amberTint} border={T.amberBorder} ink={T.amber}
              count={data.watchlist.expiring_soon.count} rule="Expiry within 7 days, SO not made"
              detail={data.watchlist.expiring_soon.most_exposed ? `Most exposed: ${data.watchlist.expiring_soon.most_exposed.party_name} · ${data.watchlist.expiring_soon.most_exposed.cnt} POs` : 'No exposure'}
              onClick={() => setDrillDown({ type: 'expiring_soon' })} />
            <AlertCard title="IN TRANSIT > 5 DAYS" unit="parcels" bg={T.blueTint} border={T.blueBorder} ink={T.blue}
              count={data.watchlist.in_transit_over.count} rule="Dispatched, no delivery scan"
              detail={`${fmtInt(data.watchlist.in_transit_over.stale_subset)} of these have had no status change in 48h`}
              onClick={() => setDrillDown({ type: 'in_transit' })} />
            <AlertCard title="AT DESTINATION > 24 H" unit="parcels" bg={T.errorTint} border={T.errorBorder} ink={T.error}
              count={data.watchlist.at_destination_over.count} rule="Reached but not marked delivered"
              detail="SLA is delivery within 24h of arrival scan"
              onClick={() => setDrillDown({ type: 'at_destination' })} />
            <div style={{ ...card, background: T.blueTint, border: `1.5px solid ${T.blueBorder}`, gridColumn: 'span 2', minWidth: 320, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.blue }}>Box & weight discrepancy</div>
                  <div style={note}>PT Invent's own count vs Shiprocket's</div>
                </div>
                <button onClick={() => setShowVariance((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: showVariance ? T.ink : T.purpleTint, color: showVariance ? '#fff' : T.purple, border: 'none', borderRadius: 8, padding: '6px 11px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {showVariance ? <EyeOff size={13} /> : <Eye size={13} />} {showVariance ? 'Hide variance' : 'View variance'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))', gap: 10, marginTop: 10 }}>
                <MiniTile label="Boxes (PT Invent)" value={fmtInt(data.discrepancy_tiles.boxes_shipped)} note="as packed" bg={T.surfaceSubtle} ink={T.ink} />
                <MiniTile label="Boxes (Shiprocket)" value={fmtInt(data.discrepancy_tiles.shiprocket_boxes)} note="no. of units, courier" bg={T.blueTint} ink={T.blue} />
                <MiniTile label="Weight (PT Invent, kg)" value={fmtInt(data.discrepancy_tiles.actual_weight_kg)} note="as packed" bg={T.surfaceSubtle} ink={T.ink} />
                <MiniTile label="Chargeable weight (kg)" value={fmtInt(data.discrepancy_tiles.chargeable_weight_kg)} note="courier billed" bg={T.blueTint} ink={T.blue} />
                {showVariance && (
                  <MiniTile label="Weight variance (+kg)" value={fmtInt(data.discrepancy_tiles.weight_variance_kg)} note={`${fmtMoney(data.discrepancy_tiles.weight_variance_freight_impact)} freight impact`} bg={T.errorTint} ink={T.error} />
                )}
              </div>
            </div>
          </div>

          {/* Discrepancy deep analytics — only rendered once "View variance" is clicked above.
              One row per shipment (not per party) so AWB + invoice number are meaningful per row. */}
          {showVariance && (
            <div style={{ ...card, padding: '18px 20px' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Discrepancy deep analytics</div>
                <div style={note}>Every shipment with a real box or weight mismatch, within the selected filters · sorted by largest weight variance first</div>
              </div>
              <div style={{ overflow: 'auto', marginTop: 14 }}>
                <ShipmentDiscrepancyTable rows={data.shipment_discrepancies} />
              </div>
            </div>
          )}

          {/* PO intake + GRN health */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ ...card, flex: '3 1 560px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>PO intake, month wise</div>
                  <div style={note}>FY {fyStartYear()}–{String(fyStartYear() + 1).slice(-2)} (Apr–Mar) · click a month for its units and PO value</div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 11, color: T.muted, alignItems: 'center' }}>
                  <Swatch color={T.purple} label="PO count" />
                  <Swatch color={T.blueLight} label="Units received" />
                </div>
              </div>
              <IntakeChart months={data.monthly_intake} selectedKey={chartMonthKey} onSelect={setChartMonthKey} />
              <IntakeCallout months={data.monthly_intake} selectedKey={chartMonthKey} scope={data.scope} rangeLabel={resolvedRanges.label || 'All time'} />
            </div>
            <div style={{ ...card, flex: '2 1 340px', padding: '18px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>GRN & discrepancy health</div>
              <div style={note}>{fmtInt(data.grn_status_split.open + data.grn_status_split.matched + data.grn_status_split.resolved)} GRNs in scope</div>
              <GrnPie split={data.grn_status_split} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                <MiniStat bg={T.amberTint} ink={T.amber} value={fmtInt(data.grn_status_split.open)} sub={`${fmtInt(data.grn_status_split.resolved)} resolved of ${fmtInt(data.grn_status_split.open + data.grn_status_split.resolved)}`} title="Discrepancies open" onClick={() => setDrillDown({ type: 'open_discrepancies' })} />
                <MiniStat bg={T.purpleTint} ink={T.purple} value={fmtInt(data.tickets.open_tickets)} sub={`avg age ${data.tickets.avg_age_days ?? '—'} days`} title="Tickets open" onClick={() => setDrillDown({ type: 'tickets' })} />
              </div>
            </div>
          </div>

          {/* Rankings + stuck */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ ...card, flex: '3 1 560px', padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Highest PO amount, {rankMode === 'party' ? 'party' : 'KAM'} wise</div>
                  <div style={note}>{resolvedRanges.label || 'All time'} · bar is PO value</div>
                </div>
                <div style={{ padding: 4, borderRadius: 999, background: T.surfaceSubtle, border: `1px solid ${T.border}`, display: 'flex', gap: 2 }}>
                  {['party', 'kam'].map((m) => (
                    <button key={m} onClick={() => setRankMode(m)} style={{ padding: '8px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: rankMode === m ? T.ink : 'transparent', color: rankMode === m ? '#fff' : T.muted }}>
                      {m === 'party' ? 'Party' : 'KAM'}
                    </button>
                  ))}
                </div>
              </div>
              <RankList rows={rankMode === 'party' ? data.rankings.by_party : data.rankings.by_kam} rankMode={rankMode} />
            </div>
            <div style={{ ...card, flex: '2 1 340px', padding: '18px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Where POs are stuck right now</div>
              <StuckMeters buckets={data.stuck_buckets} onSelectBucket={(bucket, label) => setDrillDown({ type: 'stuck', bucket, title: label })} />
              <div style={{ ...note, marginTop: 8 }}>Each PO counts once, in its oldest unfinished hop · click a bar for the PO list.</div>
            </div>
          </div>
        </>
      )}

      <DrillDownModal
        open={!!drillDown}
        onClose={closeDrillDown}
        title={drillDown ? (drillDown.title || DRILLDOWN_DEFS[drillDown.type].title) : ''}
        subtitle={drillDown ? DRILLDOWN_DEFS[drillDown.type].subtitle : ''}
        columns={drillDown ? DRILLDOWN_DEFS[drillDown.type].columns : []}
        rows={drillRows}
        loading={drillLoading}
        error={drillError}
      />
    </div>
  );
}

function FieldStack({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', color: T.muted }}>{label}</div>
      {children}
    </div>
  );
}
function selectStyle(minWidth) {
  return { minWidth, padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surfaceSubtle, color: T.ink, fontFamily: FONT, fontSize: 12.5, fontWeight: 600 };
}

function CycleStat({ label, value, target }) {
  const tone = toneFor(value, target);
  const fillPct = value === null ? 0 : Math.min(100, (value / (target * 1.6)) * 100);
  const delta = (value === null || !target) ? null : Math.round((value - target) * 10) / 10;
  return (
    <div style={{ background: T.surfaceSubtle, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 15px' }}>
      <div style={label10}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', marginTop: 6 }}>
        {fmtDays(value)} <span style={{ fontSize: 12, color: T.muted, fontWeight: 400 }}>days</span>
      </div>
      <div style={{ height: 6, background: T.border, borderRadius: 999, marginTop: 10, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${fillPct}%`, background: tone, borderRadius: 999 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
        <span style={{ color: T.muted }}>Target {target}d</span>
        {delta !== null && <span style={{ color: tone }}>{delta >= 0 ? `+${delta} over` : `${Math.abs(delta)} under`}</span>}
      </div>
    </div>
  );
}

function pct(n, total) { if (!total) return 0; return Math.round((n / total) * 100); }

function FunnelStage({ label, count, total, color, sub }) {
  const width = Math.max(6, pct(count, total));
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 600, color: T.onDarkInk }}>
        <span>{label}</span>
        <span style={{ fontSize: 11.5, color: T.onDarkSub, fontWeight: 400 }}>{sub}</span>
      </div>
      <div style={{ height: 28, background: 'rgba(255,255,255,0.07)', borderRadius: 8, marginTop: 6, position: 'relative' }}>
        <div style={{ height: '100%', width: `${width}%`, background: color, borderRadius: 8, display: 'flex', alignItems: 'center', padding: '0 11px' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1A1823' }}>{fmtInt(count)}</span>
        </div>
      </div>
    </div>
  );
}

function AlertCard({ title, unit, bg, border, ink, count, rule, detail, onClick }) {
  return (
    <div onClick={onClick} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: ink, display: 'inline-block' }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: ink, textTransform: 'uppercase' }}>{title}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: ink }}>
        {fmtInt(count)} <span style={{ fontSize: 12, opacity: 0.8 }}>{unit}</span>
      </div>
      <div style={{ fontSize: 11.5, opacity: 0.85, color: ink }}>{rule}</div>
      <div style={{ fontSize: 11.5, fontWeight: 600, borderTop: `1px solid ${border}`, paddingTop: 8, color: ink }}>{detail}</div>
    </div>
  );
}

function MiniTile({ label, value, note: n, bg, ink }) {
  return (
    <div style={{ background: bg, borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: ink, opacity: 0.8 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: ink }}>{value}</div>
      <div style={{ fontSize: 10.5, opacity: 0.8, color: ink }}>{n}</div>
    </div>
  );
}

// One row per shipment: party + PO/invoice/AWB identifiers, then box and
// weight side by side (PT Invent vs Shiprocket) with the variance called out
// next to each pair — a nonzero variance is highlighted red since this table
// only ever contains rows that already have one (see the backend filter).
function ShipmentDiscrepancyTable({ rows }) {
  const cols = ['Party', 'PO No.', 'Invoice No.', 'AWB No.', 'Box (PT Invent)', 'Box (Shiprocket)', 'Box Variance', 'Weight (PT Invent, kg)', 'Weight (Shiprocket, kg)', 'Weight Variance (kg)'];
  const gridCols = '1.3fr 0.9fr 0.9fr 1fr 0.9fr 0.9fr 0.8fr 1fr 1fr 0.9fr';
  return (
    <div style={{ minWidth: 1180 }}>
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, background: T.ink, color: '#fff', borderRadius: '12px 12px 0 0' }}>
        {cols.map((c, i) => <div key={c} style={{ padding: '11px 13px', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: i < 4 ? 'left' : 'right' }}>{c}</div>)}
      </div>
      {rows.length === 0 && <div style={{ padding: '46px 16px', textAlign: 'center', fontSize: 14, color: T.muted }}>No shipments with a box or weight discrepancy in this range.</div>}
      {rows.map((r, i) => (
        <div key={r.po_number + i} style={{ display: 'grid', gridTemplateColumns: gridCols, background: i % 2 ? T.surfaceSubtle : '#fff', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ padding: '11px 13px', fontSize: 12.5 }}>{r.party}</div>
          <div style={{ padding: '11px 13px', fontSize: 12.5 }}>{r.po_number}</div>
          <div style={{ padding: '11px 13px', fontSize: 12.5 }}>{r.invoice_no || '—'}</div>
          <div style={{ padding: '11px 13px', fontSize: 12.5 }}>{r.awb_number || '—'}</div>
          <Num v={fmtInt(r.boxes_pt_invent)} />
          <Num v={fmtInt(r.boxes_shiprocket)} color={T.blue} bold />
          <Num v={r.box_variance > 0 ? `+${fmtInt(r.box_variance)}` : fmtInt(r.box_variance)} color={r.box_variance !== 0 ? T.error : T.faint} bold />
          <Num v={fmtInt(r.weight_pt_invent_kg)} />
          <Num v={fmtInt(r.weight_shiprocket_kg)} color={T.blue} bold />
          <Num v={r.weight_variance_kg > 0 ? `+${fmtInt(r.weight_variance_kg)}` : fmtInt(r.weight_variance_kg)} color={r.weight_variance_kg !== 0 ? T.error : T.faint} bold />
        </div>
      ))}
    </div>
  );
}
function Num({ v, color, bold }) {
  return <div style={{ padding: '11px 13px', fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: color || T.ink, fontWeight: bold ? 700 : 400 }}>{v}</div>;
}

function Swatch({ color, label }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />{label}</span>;
}

function IntakeChart({ months, selectedKey, onSelect }) {
  const maxPo = Math.max(1, ...months.map((m) => m.po_count));
  const maxUnits = Math.max(1, ...months.map((m) => m.units));
  return (
    <div style={{ display: 'flex', height: 214, gap: 6, marginTop: 14, alignItems: 'flex-end' }}>
      {months.map((m) => {
        const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
        const selected = selectedKey ? selectedKey === key : false;
        const hasData = m.po_count > 0 || m.units > 0;
        return (
          <div key={key} onClick={() => hasData && onSelect(key)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', cursor: hasData ? 'pointer' : 'default', background: selected ? T.surfaceSubtle : 'transparent', borderRadius: 10, padding: '4px 0' }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: hasData ? (selected ? T.ink : T.muted) : T.faint }}>{hasData ? m.po_count : '·'}</div>
            <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', flex: 1, width: '100%', justifyContent: 'center' }}>
              <div style={{ width: '44%', borderRadius: '5px 5px 0 0', background: hasData ? (selected ? T.purple : '#9E8BC6') : T.border, height: `${hasData ? (m.po_count / maxPo) * 100 : 3}%` }} />
              <div style={{ width: '44%', borderRadius: '5px 5px 0 0', background: hasData ? (selected ? T.blueLight : '#C6DAE8') : '#F1EEF6', height: `${hasData ? (m.units / maxUnits) * 100 : 3}%` }} />
            </div>
            <div style={{ fontSize: 10.5, color: hasData ? (selected ? T.ink : T.muted) : '#C9C2D6', borderTop: `1px solid ${T.border}`, paddingTop: 6, marginTop: 4, fontWeight: selected ? 700 : 400 }}>
              {MONTH_NAMES[m.month - 1]}
            </div>
          </div>
        );
      })}
    </div>
  );
}
// Default (no bar clicked): mirrors the dashboard's active filter scope
// (Quick Range / Month / custom Range), so these tiles never show a number
// that contradicts what's selected up top. Clicking a bar in the FY-wide
// trend chart below overrides all four tiles to that one specific month.
function IntakeCallout({ months, selectedKey, scope, rangeLabel }) {
  if (!selectedKey) {
    const avgSize = scope.po_count ? Math.round(scope.units / scope.po_count) : null;
    return (
      <div style={{ background: T.surfaceSubtle, border: `1px solid ${T.border}`, borderRadius: 14, padding: '13px 15px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginTop: 12 }}>
        <CalloutItem label="POs" value={fmtInt(scope.po_count)} sub={rangeLabel} color={T.ink} />
        <CalloutItem label="Units received" value={fmtInt(scope.units)} sub={rangeLabel} color={T.blue} />
        <CalloutItem label="PO amount" value={fmtMoney(scope.amount)} sub={rangeLabel} color={T.purple} />
        <CalloutItem label="Avg PO size" value={avgSize !== null ? fmtInt(avgSize) + ' units' : '—'} sub={rangeLabel} color={T.ink} />
      </div>
    );
  }
  const idx = months.findIndex((m) => `${m.year}-${String(m.month).padStart(2, '0')}` === selectedKey);
  const m = idx >= 0 ? months[idx] : null;
  const prev = idx > 0 ? months[idx - 1] : null;
  const growth = m && prev && prev.po_count > 0 ? Math.round(((m.po_count - prev.po_count) / prev.po_count) * 100) : null;
  const monthLabel = m ? `${MONTH_NAMES[m.month - 1]} ${m.year}` : '';
  return (
    <div style={{ background: T.surfaceSubtle, border: `1px solid ${T.border}`, borderRadius: 14, padding: '13px 15px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginTop: 12 }}>
      <CalloutItem label="POs" value={fmtInt(m?.po_count)} sub={growth !== null ? `${growth >= 0 ? '+' : ''}${growth}% vs prev month` : monthLabel} color={T.ink} />
      <CalloutItem label="Units received" value={fmtInt(m?.units)} sub={monthLabel} color={T.blue} />
      <CalloutItem label="PO amount" value={fmtMoney(m?.amount)} sub={monthLabel} color={T.purple} />
      <CalloutItem label="Avg PO size" value={m && m.po_count ? fmtInt(Math.round(m.units / m.po_count)) + ' units' : '—'} sub={monthLabel} color={T.ink} />
    </div>
  );
}
function CalloutItem({ label, value, sub, color }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: T.muted }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color }}>{value}</div>
      <div style={{ fontSize: 11, color: T.muted }}>{sub}</div>
    </div>
  );
}

function GrnPie({ split }) {
  const total = split.matched + split.resolved + split.open || 1;
  const slices = [
    { label: 'Matched', value: split.matched, color: T.green },
    { label: 'Resolved', value: split.resolved, color: T.blue },
    { label: 'Open discrepancies', value: split.open, color: T.amber },
  ];
  let cum = 0;
  const cx = 84, cy = 84, r = 78;
  const arcs = slices.map((s) => {
    const startAngle = (cum / total) * 2 * Math.PI - Math.PI / 2;
    cum += s.value;
    const endAngle = (cum / total) * 2 * Math.PI - Math.PI / 2;
    const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle), y2 = cy + r * Math.sin(endAngle);
    const large = endAngle - startAngle > Math.PI ? 1 : 0;
    return { ...s, path: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} Z` };
  });
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
      <svg width={168} height={168} viewBox="0 0 168 168">
        {arcs.map((a) => <path key={a.label} d={a.path} fill={a.color} stroke="#fff" strokeWidth={2} />)}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 140 }}>
        {arcs.map((a) => (
          <div key={a.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: a.color, display: 'inline-block' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600 }}>{a.label}</div>
              <div style={{ fontSize: 11, color: T.muted }}>{pct(a.value, total)}% of GRNs</div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: a.color }}>{fmtInt(a.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function MiniStat({ bg, ink, value, sub, title, onClick }) {
  return (
    <div onClick={onClick} style={{ background: bg, borderRadius: 12, padding: '10px 12px', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: ink, opacity: 0.85 }}>{title}</div>
      <div style={{ fontSize: 21, fontWeight: 700, color: ink }}>{value}</div>
      <div style={{ fontSize: 10.5, color: ink, opacity: 0.8 }}>{sub}</div>
    </div>
  );
}

function RankList({ rows, rankMode }) {
  const top = (rows || []).slice(0, 12);
  const maxValue = Math.max(1, ...top.map((r) => r.value || 0));
  const columns = [
    { key: 'name', label: rankMode === 'party' ? 'Party' : 'KAM', align: 'left' },
    ...(rankMode === 'party' ? [
      { key: 'kam', label: 'KAM', align: 'left', render: (r) => r.kam || '—' },
    ] : []),
    {
      key: 'value', label: 'PO Value', bold: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <div style={{ width: 60, background: T.border, borderRadius: 6, height: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(4, ((r.value || 0) / maxValue) * 100)}%`, background: T.purple, borderRadius: 6 }} />
          </div>
          <span>{fmtMoney(r.value)}</span>
        </div>
      ),
    },
    { key: 'units', label: 'Units', render: (r) => fmtInt(r.units) },
    { key: 'po_count', label: 'PO Count', render: (r) => fmtInt(r.po_count) },
  ];
  return (
    <div style={{ marginTop: 14, overflow: 'auto' }}>
      <SimpleDataTable columns={columns} rows={top} />
    </div>
  );
}

const STUCK_LABELS = [
  ['po_no_so', 'PO raised, SO not made', T.error],
  ['so_not_invoiced', 'SO made, not invoiced', T.amber],
  ['invoiced_not_dispatched', 'Invoiced, not dispatched', T.blue],
  ['dispatched_not_delivered', 'Dispatched, not delivered', T.purpleMid],
  ['delivered_grn_pending', 'Delivered, GRN not done', T.purple],
];
function StuckMeters({ buckets, onSelectBucket }) {
  const maxCount = Math.max(1, ...STUCK_LABELS.map(([k]) => buckets.buckets[k] || 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
      {STUCK_LABELS.map(([key, label, color]) => {
        const count = buckets.buckets[key] || 0;
        const oldest = buckets.oldest_days[key];
        return (
          <div key={key} onClick={() => count > 0 && onSelectBucket(key, label)} style={{ cursor: count > 0 ? 'pointer' : 'default' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 600 }}>
              <span>{label}</span>
              <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 400 }}>{fmtInt(count)} POs{oldest !== undefined ? ` · oldest ${oldest}d` : ''}</span>
            </div>
            <div style={{ height: 8, background: T.border, borderRadius: 999, marginTop: 6 }}>
              <div style={{ height: '100%', width: `${Math.max(3, (count / maxCount) * 100)}%`, background: color, borderRadius: 999 }} />
            </div>
            {count > 0 && (
              <div style={{ fontSize: 11, color: T.purple, fontWeight: 600, marginTop: 4 }}>View PO list →</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
